import { type MouseEvent, useContext, useState } from "react";
import { ModalAction, ModalContent, useModal } from "~/components/Modal";
import { useSnackBar } from "~/components/SnackBar";
import { SessionContext } from "~/ory";
import { createFlowErrorHandler } from "~/utils/errorHandler";

export const ReactivateEmailDeliveryStatusSettings: React.FC = () => {
	const session = useContext(SessionContext);
	// Since the session is not updated immediately after reactivation, use isReactivated to switch the display
	const [isReactivated, setIsReactivated] = useState<boolean>(false);
	const [openModal, closeModal] = useModal();
	const [openSnackBar] = useSnackBar();

	if (!session) return <p>Unauthenticated</p>;

	if (session.account.email_delivery_status !== "suppressed" || isReactivated)
		return null;

	const submit = async (e: MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();

		try {
			const res = await fetch(
				"/api/v2/account/reactivate_email_delivery_status",
				{
					method: "PATCH",
					credentials: "same-origin",
					cache: "no-cache",
				},
			);

			if (res.status !== 200) {
				const error = await res.json();
				openModal({
					type: "error",
					title: "FAILED TO REACTIVATE EMAIL ADDRESS",
					children: (
						<>
							<ModalContent>
								<p className="text-center text-error">
									<h2 className="font-normal text-xl font-semibold mb-4">
										ERRORS
									</h2>
									<span>
										{error.error?.message ?? "Unexpected error occurred"}
									</span>
								</p>
							</ModalContent>
							<ModalAction>
								<button
									type="button"
									onClick={closeModal}
									className="btn btn-lg short btn-t4-primary rounded-full"
								>
									Close
								</button>
							</ModalAction>
						</>
					),
				});
				return;
			}

			openSnackBar({ type: "success", text: "Email address reactivated!" });
			setIsReactivated(true);
		} catch (err) {
			createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
		}
	};

	const openConfirmModal = () => {
		openModal({
			type: "warning",
			title: "Reactivate Email Address",
			children: (
				<>
					<ModalContent>
						<div className="text-center">
							<p className="font-semibold text-xl mb-8">
								Is this email address available?
							</p>
							<p className="font-normal">
								Reactivating the email address will allow you to receive emails
								again.
							</p>
							<p className="font-semibold text-warning">
								Change your email address instead of clicking the following
								button if your current email address is not available.
							</p>
						</div>
					</ModalContent>
					<ModalAction>
						<button
							type="button"
							className="btn btn-lg short btn-t4-primary rounded-full"
							onClick={(e) => {
								closeModal();
								return submit(e);
							}}
						>
							Reactivate
						</button>
						<button
							type="button"
							className="btn btn-lg short btn-t4-border rounded-full"
							onClick={closeModal}
						>
							Cancel
						</button>
					</ModalAction>
				</>
			),
		});
	};

	return (
		<div className="w-full inline-block text-center mt-8">
			<button
				type="button"
				className="btn btn-lg btn-t4-danger rounded-full"
				onClick={openConfirmModal}
			>
				Reactivate Email Address
			</button>
		</div>
	);
};
